import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { MobileNavigation } from '../components/MobileNavigation';
import { Gear, CreditCard, PaypalLogo, CheckCircle, Warning, Eye, EyeSlash, FloppyDisk } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';

import { getApiBaseUrl } from '../utils/api';
export function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    stripe_enabled: true,
    stripe_configured: false,
    stripe_mode: 'sandbox',
    paypal_enabled: false,
    paypal_configured: false,
    paypal_mode: 'sandbox'
  });
  
  const [stripeKeys, setStripeKeys] = useState({
    api_key: '',
    showKey: false
  });
  
  const [paypalKeys, setPaypalKeys] = useState({
    client_id: '',
    client_secret: '',
    showSecret: false
  });
  
  useEffect(() => {
    loadSettings();
  }, []);
  
  const loadSettings = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/settings/payments`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Load settings error:', error);
      toast.error('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };
  
  const saveStripeSettings = async () => {
    if (!stripeKeys.api_key) {
      toast.error('Please enter a Stripe API key');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/settings/stripe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          api_key: stripeKeys.api_key,
          enabled: settings.stripe_enabled,
          mode: settings.stripe_mode
        })
      });
      
      if (response.ok) {
        toast.success('Stripe settings saved!');
        setStripeKeys({ ...stripeKeys, api_key: '' });
        loadSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save Stripe settings');
      }
    } catch (error) {
      console.error('Save Stripe error:', error);
      toast.error('Failed to save Stripe settings');
    } finally {
      setSaving(false);
    }
  };
  
  const savePaypalSettings = async () => {
    if (!paypalKeys.client_id || !paypalKeys.client_secret) {
      toast.error('Please enter both PayPal Client ID and Secret');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/settings/paypal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: paypalKeys.client_id,
          client_secret: paypalKeys.client_secret,
          enabled: settings.paypal_enabled,
          mode: settings.paypal_mode
        })
      });
      
      if (response.ok) {
        toast.success('PayPal settings saved!');
        setPaypalKeys({ client_id: '', client_secret: '', showSecret: false });
        loadSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save PayPal settings');
      }
    } catch (error) {
      console.error('Save PayPal error:', error);
      toast.error('Failed to save PayPal settings');
    } finally {
      setSaving(false);
    }
  };
  
  const togglePaymentMethod = async (method, enabled) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/settings/payments/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ method, enabled })
      });
      
      if (response.ok) {
        setSettings({ ...settings, [`${method}_enabled`]: enabled });
        toast.success(`${method === 'stripe' ? 'Stripe' : 'PayPal'} ${enabled ? 'enabled' : 'disabled'}`);
      }
    } catch (error) {
      console.error('Toggle error:', error);
      toast.error('Failed to update setting');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="admin-settings">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            <Gear size={24} weight="duotone" className="text-primary" />
            <div>
              <h1 className="text-xl font-heading">PAYMENT SETTINGS</h1>
              <p className="text-sm text-muted-foreground">Configure payment integrations</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-4 py-6 space-y-6 max-w-2xl mx-auto">
        
        {/* Stripe Configuration */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#635BFF]/20 flex items-center justify-center">
                  <CreditCard size={24} weight="duotone" className="text-[#635BFF]" />
                </div>
                <div>
                  <CardTitle className="text-lg">Stripe</CardTitle>
                  <CardDescription>Credit/Debit card payments</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {settings.stripe_configured ? (
                  <Badge className="bg-green-500/20 text-green-500">
                    <CheckCircle size={14} className="mr-1" /> Configured
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-500">
                    <Warning size={14} className="mr-1" /> Not Configured
                  </Badge>
                )}
                <Switch
                  checked={settings.stripe_enabled}
                  onCheckedChange={(checked) => togglePaymentMethod('stripe', checked)}
                  disabled={!settings.stripe_configured}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-muted/30 rounded-lg text-sm">
              <p className="text-muted-foreground">
                Get your API keys from{' '}
                <a 
                  href="https://dashboard.stripe.com/apikeys" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Stripe Dashboard → API Keys
                </a>
              </p>
            </div>
            
            <div>
              <Label>Stripe Secret Key</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Input
                    type={stripeKeys.showKey ? 'text' : 'password'}
                    value={stripeKeys.api_key}
                    onChange={(e) => setStripeKeys({ ...stripeKeys, api_key: e.target.value })}
                    placeholder={settings.stripe_configured ? '••••••••••••••••' : 'sk_test_... or sk_live_...'}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setStripeKeys({ ...stripeKeys, showKey: !stripeKeys.showKey })}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                  >
                    {stripeKeys.showKey ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <Button onClick={saveStripeSettings} disabled={saving || !stripeKeys.api_key}>
                  <FloppyDisk size={18} className="mr-2" />
                  Save
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Use <code className="bg-muted px-1 rounded">sk_test_</code> for sandbox, <code className="bg-muted px-1 rounded">sk_live_</code> for production
              </p>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
              <span className="text-sm">Mode</span>
              <Badge variant={settings.stripe_mode === 'sandbox' ? 'secondary' : 'default'}>
                {settings.stripe_mode === 'sandbox' ? 'Sandbox (Testing)' : 'Live (Production)'}
              </Badge>
            </div>
          </CardContent>
        </Card>
        
        {/* PayPal Configuration */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#0070BA]/20 flex items-center justify-center">
                  <PaypalLogo size={24} weight="duotone" className="text-[#0070BA]" />
                </div>
                <div>
                  <CardTitle className="text-lg">PayPal</CardTitle>
                  <CardDescription>PayPal account payments</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {settings.paypal_configured ? (
                  <Badge className="bg-green-500/20 text-green-500">
                    <CheckCircle size={14} className="mr-1" /> Configured
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-500">
                    <Warning size={14} className="mr-1" /> Not Configured
                  </Badge>
                )}
                <Switch
                  checked={settings.paypal_enabled}
                  onCheckedChange={(checked) => togglePaymentMethod('paypal', checked)}
                  disabled={!settings.paypal_configured}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-muted/30 rounded-lg text-sm">
              <p className="text-muted-foreground">
                Get your credentials from{' '}
                <a 
                  href="https://developer.paypal.com/dashboard/applications" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  PayPal Developer Dashboard → Apps & Credentials
                </a>
              </p>
            </div>
            
            <div>
              <Label>PayPal Client ID</Label>
              <Input
                value={paypalKeys.client_id}
                onChange={(e) => setPaypalKeys({ ...paypalKeys, client_id: e.target.value })}
                placeholder={settings.paypal_configured ? '••••••••••••••••' : 'Enter Client ID'}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>PayPal Client Secret</Label>
              <div className="relative mt-1">
                <Input
                  type={paypalKeys.showSecret ? 'text' : 'password'}
                  value={paypalKeys.client_secret}
                  onChange={(e) => setPaypalKeys({ ...paypalKeys, client_secret: e.target.value })}
                  placeholder={settings.paypal_configured ? '••••••••••••••••' : 'Enter Client Secret'}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setPaypalKeys({ ...paypalKeys, showSecret: !paypalKeys.showSecret })}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                >
                  {paypalKeys.showSecret ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <Button onClick={savePaypalSettings} disabled={saving || !paypalKeys.client_id || !paypalKeys.client_secret} className="w-full">
              <FloppyDisk size={18} className="mr-2" />
              Save PayPal Settings
            </Button>
            
            <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
              <span className="text-sm">Mode</span>
              <Badge variant={settings.paypal_mode === 'sandbox' ? 'secondary' : 'default'}>
                {settings.paypal_mode === 'sandbox' ? 'Sandbox (Testing)' : 'Live (Production)'}
              </Badge>
            </div>
          </CardContent>
        </Card>
        
        {/* Info Card */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <CheckCircle size={24} className="text-primary flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-foreground mb-1">Security Note</p>
                <p className="text-muted-foreground">
                  Payment credentials are stored securely and encrypted. They are never exposed in API responses 
                  or frontend code. Use sandbox/test keys for development and switch to live keys only when ready for production.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
      </div>
      
      <MobileNavigation userRole="admin" />
    </div>
  );
}
